/**
 * `lib/site` — where the MARKETING site is, and the only place the dashboard
 * names it.
 *
 * Story 3.10, DECISION-102 (Q-070).
 *
 * The dashboard and the marketing site are DIFFERENT HOSTS. `leadgible.com` is
 * served by `leadgible-production-site`; the dashboard is a separate Worker,
 * and `app.leadgible.com` is a redirect rather than a Custom Domain on purpose
 * (Story 1.8's finding — `APP_ORIGIN` is one value serving as both the CORS
 * allow-origin and Better Auth's `trustedOrigins`, so a dashboard served at a
 * hostname it does not name cannot call its own API).
 *
 * That separation is why this file exists. Until 2026-08-31 `SignUp.tsx` linked
 * `/terms`, `/privacy` and `/dpa` as paths on the DASHBOARD origin, which
 * serves an SPA and has none of them, and Story 13.7 building the real pages on
 * the marketing origin did not fix them by existing. The app had no configured
 * site origin at all — `lib/api.ts` read `VITE_API_ORIGIN` and nothing else —
 * so there was nowhere for a correct link to come from.
 *
 * BUILD-TIME, like every `VITE_*`. Vite substitutes it when the bundle is
 * built, so changing it means a rebuild and a redeploy, not a restart.
 *
 * The paths passed to `siteUrl` are the ones `packages/site/src/pages.mjs`
 * declares in `PAGES`, and `tests/signup-legal-links.test.ts` holds the two
 * together in both directions — a link here must name a page that exists, and
 * it must still name one after somebody edits either file.
 */

/**
 * The default is PRODUCTION, and it is the one place in the app that is not
 * staging by default.
 *
 * `packages/site/build.mjs` defaults its own `SITE_ORIGIN` to the staging site,
 * and that was right when it was written: Story 13.6 had not landed and the
 * apex answered nothing, so a default that resolved beat one that was
 * aspirational. The apex has served since 2026-08-31, and these two links are
 * legal text a store reviewer follows — pointing them at a `workers.dev` mirror
 * that carries `X-Robots-Tag: noindex` would be answering the question wrong in
 * a way that still returns 200.
 */
const DEFAULT_SITE_ORIGIN = 'https://leadgible.com'

/**
 * Resolve the configured origin, or fall back.
 *
 * Exported for its own sake: this is the half with edge cases, and it is a pure
 * function so the cases can be asked directly rather than by rebuilding the app
 * with five different environments.
 *
 * EMPTY IS NOT CONFIGURED, and that is the case worth spelling out. `??` alone
 * would pass an empty string through — `VITE_SITE_ORIGIN=` in a `.env` file is
 * a string, not `undefined` — and `siteUrl('/terms')` would then return
 * `/terms`, a path on the dashboard origin. That is precisely the bug this
 * module was written to remove, reintroduced by a variable somebody set and
 * left blank. A trailing slash is trimmed for the same reason a blank is
 * refused: `https://leadgible.com//terms` is somebody else's redirect to debug.
 */
export function resolveSiteOrigin(raw: string | undefined): string {
  const configured = raw?.trim().replace(/\/+$/, '')
  return configured ? configured : DEFAULT_SITE_ORIGIN
}

/** The marketing site's origin, resolved once at module load. */
export const SITE_ORIGIN = resolveSiteOrigin(
  import.meta.env['VITE_SITE_ORIGIN'] as string | undefined,
)

/**
 * A URL on the marketing site.
 *
 * `path` is a rooted path — `/terms`, `/privacy` — matching a `slug` in
 * `packages/site/src/pages.mjs`. It is not validated here: a typo is a dead
 * link either way, and the test that catches it can see both files at once,
 * which this function cannot.
 */
export function siteUrl(path: string): string {
  return `${SITE_ORIGIN}${path}`
}

/**
 * Whether the marketing site serves the Data Processing Agreement yet — Story 26.3.
 *
 * `packages/site/src/legal.mjs`'s `DPA.open` is the switch; this is its mirror, because
 * the dashboard cannot read the site's build. `tests/signup-legal-links.test.ts` holds the
 * two equal, so the signup form's DPA sentence appears exactly when `/dpa` does — and
 * the site's switch opens only on the operator's approval of the exact text (DECISION-174).
 *
 * Open since 2026-09-29 (DECISION-176). The link is `siteUrl('/dpa')`, and `build.mjs` names
 * the apex as the site in BOTH environments, so a dashboard build carrying this reaches
 * `leadgible.com/dpa` — served from Story 26.7's site deploy, not from this switch.
 */
export const DPA_OPEN: boolean = true
